import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

interface AccessibilityContextValue {
  reducedMotionEnabled: boolean;
  setReducedMotionEnabled: (enabled: boolean) => void;
  toggleReducedMotion: () => void;
  customCursorEnabled: boolean;
  setCustomCursorEnabled: (enabled: boolean) => void;
  toggleCustomCursor: () => void;
  announce: (message: string) => void;
}

const AccessibilityContext = createContext<AccessibilityContextValue | undefined>(undefined);

const STORAGE_KEY = "oneform-accessibility-reduced-motion";
const CUSTOM_CURSOR_STORAGE_KEY = "oneform-accessibility-custom-cursor";

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const [statusMessage, setStatusMessage] = useState("");
  const hasPendingAnnouncement = useRef<number | null>(null);
  const [hasExplicitPreference, setHasExplicitPreference] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      return window.localStorage.getItem(STORAGE_KEY) !== null;
    } catch (error) {
      console.warn("Accessing localStorage failed", error);
      return false;
    }
  });

  const [reducedMotionEnabled, setReducedMotionState] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      const storedValue = window.localStorage.getItem(STORAGE_KEY);
      if (storedValue !== null) {
        return storedValue === "true";
      }
    } catch (error) {
      console.warn("Accessing localStorage failed", error);
    }

    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  });

  const [customCursorEnabled, setCustomCursorState] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      return window.localStorage.getItem(CUSTOM_CURSOR_STORAGE_KEY) === "true";
    } catch (error) {
      console.warn("Accessing localStorage failed", error);
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const root = window.document.documentElement;
    root.classList.toggle("reduced-motion", reducedMotionEnabled);
  }, [reducedMotionEnabled]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = (event: MediaQueryListEvent) => {
      if (!hasExplicitPreference) {
        setReducedMotionState(event.matches);
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [hasExplicitPreference]);

  const announce = useCallback((message: string) => {
    setStatusMessage("");

    if (typeof window === "undefined") {
      setStatusMessage(message);
      return;
    }

    if (hasPendingAnnouncement.current) {
      window.clearTimeout(hasPendingAnnouncement.current);
    }

    hasPendingAnnouncement.current = window.setTimeout(() => {
      setStatusMessage(message);
      hasPendingAnnouncement.current = null;
    }, 50);
  }, []);

  useEffect(() => {
    return () => {
      if (hasPendingAnnouncement.current && typeof window !== "undefined") {
        window.clearTimeout(hasPendingAnnouncement.current);
      }
    };
  }, []);

  const updateReducedMotion = useCallback(
    (enabled: boolean) => {
      setReducedMotionState(enabled);
      setHasExplicitPreference(true);

      try {
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.setItem(STORAGE_KEY, String(enabled));
        }
      } catch (error) {
        console.warn("Persisting reduced motion preference failed", error);
      }

      announce(`Motion effects ${enabled ? "disabled" : "enabled"}.`);
    },
    [announce]
  );

  const toggleReducedMotion = useCallback(() => {
    updateReducedMotion(!reducedMotionEnabled);
  }, [reducedMotionEnabled, updateReducedMotion]);

  const updateCustomCursor = useCallback(
    (enabled: boolean) => {
      setCustomCursorState(enabled);

      try {
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.setItem(CUSTOM_CURSOR_STORAGE_KEY, String(enabled));
        }
      } catch (error) {
        console.warn("Persisting custom cursor preference failed", error);
      }

      announce(`Custom cursor ${enabled ? "enabled" : "disabled"}.`);
    },
    [announce]
  );

  const toggleCustomCursor = useCallback(() => {
    updateCustomCursor(!customCursorEnabled);
  }, [customCursorEnabled, updateCustomCursor]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === CUSTOM_CURSOR_STORAGE_KEY) {
        setCustomCursorState(event.newValue === "true");
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const value = useMemo<AccessibilityContextValue>(() => ({
    reducedMotionEnabled,
    setReducedMotionEnabled: updateReducedMotion,
    toggleReducedMotion,
    customCursorEnabled,
    setCustomCursorEnabled: updateCustomCursor,
    toggleCustomCursor,
    announce,
  }), [
    announce,
    customCursorEnabled,
    reducedMotionEnabled,
    toggleCustomCursor,
    toggleReducedMotion,
    updateCustomCursor,
    updateReducedMotion,
  ]);

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {statusMessage}
      </div>
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext);

  if (context === undefined) {
    throw new Error("useAccessibility must be used within an AccessibilityProvider");
  }

  return context;
}
